import type { ProvidesLine, StatCell, Unit } from "@sins2-atlas/game-data";
import type { BestDirection } from "./compare";
import { dataset } from "./dataset";
import { exactNumber } from "./format";
import type { StatFrame } from "./frame";
import { type FrameStat, frameCell, frameCloaking, frameProvides } from "./frame-fold";

// The stat sheet, declared ONCE (ADR 0005: Game Order). The roster's columns, the entity
// page's cards and the comparison's rows are three renderings of this one model — they used
// to be three independent declarations, and they had drifted: at 60a8c24 the same unit read
// Durability→Shields→Armor→Armor Strength→Hull on /unit but Hull→Armor→Shields→Durability on
// /compare and /roster, under two different names for the group of price rows, with /roster
// missing Armor Strength entirely. The Game Order commit (a394d10) reached the unit page and
// never the other two, and nothing failed.
//
// So: order, grouping, labels, cells and best-direction live here, and a surface may only
// choose HOW to draw a row, never which rows there are or in what sequence. Anything genuinely
// per-surface (the roster's exotics column, the unit page's fabricated totals, the comparison's
// Strikecraft block) stays with that surface as an explicit addition to a named group.
//
// Since ADR 0008 the Roster is read as two views, Ships and Structures, and each row also
// declares which of the two it applies to (`kinds`). That declaration lives here and nowhere
// else: a view asks `statModelFor` for its kind's rows, the comparison asks for the union of
// the kinds in its set, and no surface may restate which rows a kind gets.

/** The two views over the Roster (CONTEXT.md: Ships, Structure). The game's own pair — its
 * build windows are "Build Ships" and "Build Orbital Structures" — with starbases on the
 * Structures side, where the player files list them. */
export const VIEW_KINDS = ["ships", "structures"] as const;
export type ViewKind = (typeof VIEW_KINDS)[number];

/** Which view a unit belongs to, from its Class: Structure and Starbase are Structures,
 * every other rung of the ladder (Strikecraft through Titan) is Ships. */
export function viewKindOf(unit: Pick<Unit, "kind">): ViewKind {
  return unit.kind === "structure" || unit.kind === "starbase" ? "structures" : "ships";
}

const BOTH_KINDS: readonly ViewKind[] = VIEW_KINDS;
const SHIPS_ONLY: readonly ViewKind[] = ["ships"];
const STRUCTURES_ONLY: readonly ViewKind[] = ["structures"];

/** One row of the stat sheet. `id` is load-bearing in three places at once: the roster's
 * column id, its `?sort=` token (a public URL API — see SORT_COLUMNS in routes/roster.tsx),
 * and the key into DERIVED_COLUMN_DISCLOSURES for the formula tooltip. */
export interface StatRowSpec {
  id: string;
  /** The full name, used wherever there is room: the unit page and the comparison rail. */
  label: string;
  /** A denser spelling for the roster's column head. Earned per column, never assumed —
   * a row without one renders `label` everywhere. */
  shortLabel?: string;
  /** The row's name as the game labels it for THIS unit, where the game's own label varies
   * per unit (the Orbit row reads "Civilian Orbit Slot Cost" on an extractor and "Military
   * Orbit Slot Cost" on a defense platform). `label` stays the row's one name for a surface
   * that cannot vary it — the table's column head spans both kinds — and for a comparison
   * whose units disagree; `rowLabel` is the rule. */
  unitLabel?: (unit: Unit) => string;
  /** Which way "best" points when the comparison marks a winner. Absent means never marked:
   * the prototype's rule is that only performance and efficiency rows compete, because
   * "cheapest" across a titan and a frigate says nothing. */
  best?: BestDirection;
  /** The views this row applies to (ADR 0008). A row is ships-only when it is Not
   * Applicable on every structure — a fact about the game, measured over all 65 (Bombing,
   * Pop. damage, Supply, DPS / supply). Speed is both: the Orkulus Starbase moves. */
  kinds: readonly ViewKind[];
  cell: (unit: Unit, frame: StatFrame) => StatCell;
  format?: (value: number) => string;
}

export interface StatGroupSpec {
  title: string;
  rows: StatRowSpec[];
  /** Present on a list-valued group (Provides, Issue 30): its rows are not declared here
   * but read off the units a surface is about to render — see `lineRows`. */
  lines?: LineGroupSpec;
}

/** A list-valued group's declaration (CONTEXT.md: Provides): which views it applies to and
 * each unit's own lines, in the dataset's order. The labels are the game's own tooltip
 * headers, so the model cannot enumerate them — a surface renders one row per label
 * present among its units. A list of facts, not stats: a unit without a label's line has
 * no cell at all, never a Not Applicable one (ADR 0003 states are for stats). */
interface LineGroupSpec {
  kinds: readonly ViewKind[];
  /** This unit's own lines under a frame. Frame-aware since Issue 48: a line gated on a Ship
   * Item is stated exactly when that hull's Loadout holds it, so which rows a surface draws
   * is a question about the frame in front of the reader, not about the dataset alone. */
  of: (unit: Unit, frame: StatFrame) => readonly ProvidesLine[];
}

/** One unit as a surface is about to draw it: the hull and the frame it is drawn under. The
 * comparison gives each column its own frame (ADR 0011) and may hold the same hull twice
 * under two of them, so a list-valued group is read over these pairs rather than over units. */
export interface UnitInFrame {
  unit: Unit;
  frame: StatFrame;
}

/** One rendered row of a list-valued group: a label present among the units, and each
 * unit's line under it (undefined where the unit states nothing under that label). */
export interface LineRow {
  /** The group's namespace plus the label — a stable column id, never a positional index. */
  id: string;
  label: string;
  lineOf: (unit: Unit, frame: StatFrame) => ProvidesLine | undefined;
}

/** The Provides header's disclosure, worded from the glossary entry: composed facts in the
 * game's words, and blank where the file says nothing. */
export const PROVIDES_NOTE =
  "What the structure does for its owner, in the game's own tooltip words. Blank where the " +
  "structure file states nothing.";

/** The rows a list-valued group renders for these units: the union of the labels their
 * lines carry, in first-seen order (each unit's own order is the dataset's). This is the
 * rule for a surface rendering ONE unit or four — the entity page's card and the
 * comparison's rows — so the two cannot disagree on which labels appear. The dense table
 * renders the same group as one column instead (`lineColumn`, ADR 0008 addendum). A stat
 * group, or a set with no lines, yields no rows. */
export function lineRows(group: StatGroupSpec, drawn: readonly UnitInFrame[]): LineRow[] {
  const of = group.lines?.of;
  if (of === undefined) return [];
  const labels: string[] = [];
  for (const { unit, frame } of drawn) {
    for (const line of of(unit, frame)) {
      if (!labels.includes(line.label)) labels.push(line.label);
    }
  }
  return labels.map((label) => ({
    id: `${group.title.toLowerCase()}:${label}`,
    label,
    lineOf: (unit, frame) => of(unit, frame).find((line) => line.label === label),
  }));
}

/** A list-valued group as ONE column, which is how the dense table renders it (ADR 0008
 * addendum, 2026-09-06): the label moves out of the column head and into the cell, beside
 * its value — every game word survives, it just sits somewhere else. Measured before the
 * change: a column per label was 14 columns over the 65 structures holding 31 filled cells,
 * eleven of them with exactly one value in the whole table, and 2,988 px of a 5,402 px
 * table. */
export interface LineColumn {
  /** The group's namespace — `lineRows` prefixes its per-label ids with the same word, so
   * a surface that restated the other rendering would show up as an extra `provides:*`. */
  id: string;
  /** The column's head: the group's own word ("Provides"), the game's own words now being
   * inside the cells. */
  label: string;
  /** This unit's own lines under the frame it is drawn in, in the dataset's order — never
   * the union across units, and never a placeholder for a label some other structure
   * carries. A unit with nothing to state yields none, and its cell is empty (never an ADR
   * 0003 glyph: Provides is a list of facts, not a stat). */
  linesOf: (unit: Unit, frame: StatFrame) => readonly ProvidesLine[];
}

/** The one column a list-valued group renders on the table, or undefined where there is
 * nothing to state: a stat group, or a set of units none of which carries a line (the same
 * drop rule as `lineRows` — a heading over nothing reads as a gap where the honest reading
 * is that the files say nothing). Both rules live here side by side so that no surface
 * restates either: the table calls this one, the entity page and the comparison `lineRows`. */
export function lineColumn(
  group: StatGroupSpec,
  drawn: readonly UnitInFrame[],
): LineColumn | undefined {
  const of = group.lines?.of;
  if (of === undefined) return undefined;
  if (!drawn.some(({ unit, frame }) => of(unit, frame).length > 0)) return undefined;
  return { id: group.title.toLowerCase(), label: group.title, linesOf: of };
}

/** A row whose cells the frame can move (FrameStat) — folded on the page under whatever the
 * reader holds; the Cost rows are here too since a Loadout's price joined the hull's (Issue 47). */
function frameRow(
  id: FrameStat,
  label: string,
  /** Undefined on a row the comparison never marks a winner on — the Cost rows, where
   * "cheapest" across a titan and a frigate says nothing (the prototype's rule). */
  best: BestDirection | undefined,
  extra: Partial<StatRowSpec> = {},
): StatRowSpec {
  return {
    id,
    label,
    ...(best === undefined ? {} : { best }),
    kinds: BOTH_KINDS,
    cell: (unit, frame) => frameCell(unit, frame, id),
    ...extra,
  };
}

/** A frame-invariant row (costs, supply, build time, durability): no research modifies it,
 * so the Base cell is true in both frames. */
function factRow(
  id: string,
  label: string,
  pick: (unit: Unit) => StatCell,
  extra: Partial<StatRowSpec> = {},
): StatRowSpec {
  return { id, label, kinds: BOTH_KINDS, cell: (unit) => pick(unit), ...extra };
}

/** The common tail of the game's two per-kind labels (CONTEXT.md: Orbit Slot) — the
 * column head, and the label of a comparison mixing both kinds. */
const ORBIT_SLOT_COST = "Orbit Slot Cost";

/** The Orbit Slot Cost cell: the structure block's own (Computed 1 to 15, or a starbase's
 * Not Applicable "occupies a virtual military slot" — both the dataset's words). A ship
 * carries no block; its cell is only ever read on a comparison that sets a ship beside a
 * structure (the union rule, ADR 0008), and is a fact about the game, never a blank. */
function slotCostCell(unit: Unit): StatCell {
  return (
    unit.structure?.slotCost ?? { state: "not_applicable", reason: "a ship occupies no orbit slot" }
  );
}

/** The game's own label for the unit's slot kind (`tooltip.structure_slot_cost_labels.*`,
 * shipped once at dataset.orbitSlotLabels). A starbase's virtual slot is a military one —
 * its `virtual_slot_type`, and the military build group the game lists it under — so it
 * reads under the military label beside its Not Applicable cost. */
function slotCostLabel(unit: Unit): string {
  const labels = dataset.orbitSlotLabels;
  switch (unit.structure?.slotType) {
    case "civilian":
      return labels.civilian.cost;
    case "military":
    case "virtual":
      return labels.military.cost;
    case undefined:
      return ORBIT_SLOT_COST;
  }
}

/** A row's label for the units a surface is about to render it for: the per-unit spelling
 * where the row has one and every unit agrees on it (one unit always agrees with itself),
 * else the row's one name. The unit page and the comparison rail both read this, so a
 * civilian-beside-military comparison and a structure's own page cannot word the same row
 * by two different rules. */
export function rowLabel(row: StatRowSpec, units: readonly Unit[]): string {
  if (row.unitLabel === undefined) return row.label;
  const labels = new Set(units.map(row.unitLabel));
  const [only] = labels;
  return labels.size === 1 && only !== undefined ? only : row.label;
}

/** A cloak quality as the game words it: its rung name with the number beside it —
 * "Standard (2)" (ADR 0013 decision 3). The rung names are the dataset's, resolved once at
 * extraction from `tooltip.unit.cloaking_status.*`, and the pairing of number with name is
 * the game's own HUD spelling ("2: Standard") rearranged so the WORD leads, which is what a
 * unit tooltip prints.
 *
 * A quality above the top rung is shown AT the top rung: the ladder the game names runs 0 to
 * 4 and the Anathema's cloaking crest is 5, a buffer rather than a violation. That clamp is
 * ours, so the cell carries it as a second Assumption Caption (compute's
 * CLOAK_ABOVE_LADDER_CAPTION). */
function cloakRung(quality: number): string {
  const rungs = dataset.cloakLabels.rungs;
  const index = Math.min(Math.max(Math.round(quality), 0), rungs.length - 1);
  return `${rungs[index] ?? rungs[rungs.length - 1]} (${quality})`;
}

export const STAT_MODEL: readonly StatGroupSpec[] = [
  {
    // FIRST, and ONE group for everything a hull costs to put on the field (user, 2026-09-07:
    // "Fleet is really the cost in supply and the cost in time. Rename the whole section
    // 'cost'"): the game's Price rows first, then the supply and the seconds the older
    // "Fleet" group held.
    //
    // First because the reader's own question comes first (user, 2026-09-07): what a hull
    // costs is what they are deciding about, and every other group answers "and what do you
    // get for it". This also settles what Efficiency's move would otherwise have broken —
    // with Cost at the top, DPS / cost still reads AFTER the prices it divides by, so ADR
    // 0005's derived-follows-its-source rule survives both changes rather than losing to one.
    // The game does not print it here: its build-menu tooltip leads with Supply Cost and
    // Price, but its hover+ALT view — the one this sheet follows — leads with Durability.
    // A deliberate non-copy, recorded in ADR 0005.
    //
    // The word is OURS, and deliberately so — this reverses ADR 0005's "the group is Price,
    // not Cost", which took the game's word for the game's three resource rows. The merged
    // group covers more than the game's Price does, so the game's word would now be wrong
    // about two of its five rows. Nothing else moves: the row ids, their `?sort=` tokens and
    // their labels are the ones both groups already shipped.
    //
    // Frame rows since Issue 47 (ADR 0012: item cost is part of the unit's cost). Research
    // and Corruption move no price, so these read the hull's own figures everywhere except
    // where a Loadout exists — the entity page and one comparison column. Both roster views
    // therefore still mean the hull, which is the same Base rule as every other stat there.
    title: "Cost",
    rows: [
      frameRow("credits", "Credits", undefined, { format: exactNumber }),
      frameRow("metal", "Metal", undefined, { format: exactNumber }),
      frameRow("crystal", "Crystal", undefined, { format: exactNumber }),
      factRow("supply", "Supply", (unit) => unit.supplyCost, {
        format: exactNumber,
        kinds: SHIPS_ONLY,
      }),
      factRow("buildTime", "Build time (s)", (unit) => unit.buildTime, {
        shortLabel: "Build (s)",
        format: exactNumber,
      }),
    ],
  },
  {
    // What the game's build tooltip states first about a structure (ADR 0008, CONTEXT.md:
    // Orbit Slot): the planet capacity it consumes. Directly after Cost since 2026-09-07 —
    // it opened the Structures sheet before Cost was moved above it, and the two belong
    // together: a slot is a cost too, in the one currency a planet issues.
    // Structures-only — a ship occupies no slot — and marked best-lower, the one reading a
    // slot count supports (fewer slots for a building of the same kind). Exact: a count of
    // 1 to 15, never a rounded measure. The label is the game's per kind (unitLabel).
    title: "Orbit",
    rows: [
      factRow("slotCost", ORBIT_SLOT_COST, slotCostCell, {
        best: "lower",
        kinds: STRUCTURES_ONLY,
        format: exactNumber,
        unitLabel: slotCostLabel,
      }),
    ],
  },
  {
    // The game's own hover+ALT sequence, which is the damage pipeline outside-in: Durability
    // governs how Pierce meets every pool, then shields absorb, then armor (where Armor
    // Strength applies), then hull, then the Corruption pool (the last of the game's pool
    // rows, observed 2026-09-04). EHP is ours and appends after the five it summarises.
    title: "Survivability",
    rows: [
      factRow("durability", "Durability", (unit) => unit.durability, { best: "higher" }),
      frameRow("shields", "Shields", "higher"),
      frameRow("armor", "Armor", "higher"),
      frameRow("armorStrength", "Armor Strength", "higher"),
      frameRow("hull", "Hull", "higher"),
      // The pool's size (CONTEXT.md Corruption): a fact no research modifies, rendered
      // even where the game omits the row at 0 (ADR 0005). Exact like EHP — 10,000 on a
      // titan reads as a size, not a rounded measure. No best mark: a bigger pool is
      // more room for the Eidolon to fill, which is neither better nor worse.
      factRow("corruption", "Corruption", (unit) => unit.corruptionPool, {
        format: exactNumber,
      }),
      // Exact, unlike every other pool: EHP's magnitude outgrew the shared
      // 4-significant-digit formatter.
      frameRow("ehp", "EHP", "higher", { format: exactNumber }),
    ],
  },
  {
    // What a unit does for its owner (CONTEXT.md: Provides; ADR 0008 as amended): a list of
    // facts in the game's own tooltip headers ("Can Build Ships"), composed at extraction.
    // Not stats — so the group declares no rows; a surface renders one row per label present
    // among its units (lineRows), and a unit without a line shows nothing, never a glyph.
    // BOTH kinds since 2026-09-06: the game prints these lines for ships too — its own build
    // preview lists what a Jusotra Fabricator Cruiser can build. The units with no line are
    // unaffected: an empty list renders nothing at all.
    // Frame-aware since Issue 48 (ADR 0012): a line the game gates on an equipped Ship Item
    // is stated exactly when this hull's Loadout holds it. The roster views carry no Loadout,
    // so they show the unconditional lines and a starbase's factory stays blank there.
    title: "Provides",
    rows: [],
    lines: { kinds: BOTH_KINDS, of: frameProvides },
  },
  {
    title: "Combat",
    rows: [
      frameRow("dps", "DPS", "higher"),
      frameRow("novaDamage", "Nova Damage", "higher", { shortLabel: "Nova" }),
      frameRow("range", "Range", "higher"),
      frameRow("bombingRate", "Bombing dmg/s", "higher", {
        shortLabel: "Bombing",
        kinds: SHIPS_ONLY,
      }),
      frameRow("populationRate", "Pop. dmg/s", "higher", {
        shortLabel: "Pop. dmg",
        kinds: SHIPS_ONLY,
      }),
      frameRow("speed", "Speed", "higher"),
    ],
  },
  {
    // Ours entirely, and kept as its own labelled group rather than folded into Cost: these
    // are the Insight Layer, the thing the game has no counterpart for at all. Their names
    // stay ours too (`DPS / cost`, `Cost / HP`) — the ids are already shipped in URLs.
    //
    // It follows Combat directly (user, 2026-09-07: "Efficiency should come after DPS"), and
    // DPS is Combat's first row. So the derived combat figures — DPS / supply, DPS / cost —
    // read beside the combat figures they are derived FROM, and the reader who has just
    // taken in a hull's damage output gets its value-for-money in the next breath. Nothing is
    // given up for it: Cost now opens the sheet, so DPS / cost and Cost / HP still follow the
    // price rows they divide by, and ADR 0005's "our insight layer follows the game rows it
    // builds on" holds for BOTH of an Efficiency row's sources rather than only one.
    title: "Efficiency",
    rows: [
      frameRow("dpsPerSupply", "DPS / supply", "higher", {
        shortLabel: "DPS/supply",
        kinds: SHIPS_ONLY,
      }),
      frameRow("dpsPerCost", "DPS / cost", "higher", { shortLabel: "DPS/cost" }),
      frameRow("costPerHp", "Cost / HP", "lower", { shortLabel: "Cost/HP" }),
    ],
  },
  {
    // Second to last, and its own group (ADR 0013, CONTEXT.md: Cloak Detection). Observed in
    // game 2026-09-07 (.scratch/sins2-atlas/in-game-protocol-cloak.md): a Viturska Empire Lab
    // reads Durability → Armor Points → Hull Points → Empire Research Points → Cloak
    // Detection, so the game closes its unit tooltip with this row, after the lines saying
    // what the unit provides. A Jikara Navigator prints no such row at all — which is why a
    // unit without the stat reads Not Applicable rather than Computed 0. Only Cloaking now
    // follows it, by the reader's choice rather than the game's (see that group).
    //
    // The group's title and its one row share the game's word because the game has only the
    // one; the title comes from the dataset for the same reason the Orbit labels do — the
    // words are the game's, resolved once at extraction (ADR 0005).
    //
    // Both kinds, so this is a Ships column AND a 24th Structures column. Deliberate: the 23
    // came from collapsing Provides, not from a ceiling, and eight of the fourteen carriers
    // are research labs (ADR 0013). No best mark — every carrier reads 3, so "most cloak
    // detection" distinguishes nothing — and exact, a rating rather than a rounded measure.
    title: dataset.cloakLabels.detection,
    rows: [
      // A frame row since Issue 55: the Eidolon Cloak Detector Array is the one thing in
      // the game that permanently moves this stat, and it moves the STATE as well as the
      // number — the six Eidolon hulls that can buy it carry no innate detection at all, so
      // a fitted one turns "has no cloak detection" into 5. A Loadout does not ride a link
      // (ADR 0012) and neither roster table declares `?items=`, so this column reads the
      // bare hull on Ships and Structures and moves only on an entity page or in one
      // comparison column.
      frameRow("cloakDetection", dataset.cloakLabels.detection, undefined, {
        format: exactNumber,
      }),
    ],
  },
  {
    // LAST, and a deliberate non-copy (user, 2026-09-07: "Corruption and Cloaking cards
    // should come last on the ship page"). The game prints Cloaking ABOVE Durability, before
    // every stat row — observed on a Jikara Navigator 2026-09-07, protocol
    // .scratch/sins2-atlas/in-game-protocol-cloak.md — and that reading is not in doubt; the
    // user has chosen not to follow it. Six of 166 units carry the stat at all, so a group
    // that opened every sheet was a row of "cannot cloak" or nothing above the pools every
    // reader actually came for. Put here it sits beside Cloak Detection instead, which is
    // the other half of the same subject, and the two close the sheet together (ADR 0005's
    // amendment records the trade; ADR 0013's "opposite ends of the tooltip" is amended
    // with it).
    //
    // Ships-only, because all six hulls the game gives a `cloak_ability` are ships.
    //
    // The row is the first on the site whose ADR 0003 state the reader's selection decides
    // (ADR 0013 decision 2): four of the six cannot cloak until a Research Subject is ticked
    // or a Ship Item fitted, so the cell reads Not Applicable "cannot cloak" until one is —
    // never a 0, and never the game's live-status "Not Cloaked" (CONTEXT.md: Cloaking).
    //
    // No best mark: a higher quality IS better, but the number only becomes a rung after an
    // enemy's Cloak Detection is subtracted, and marking a winner would state a comparison
    // the caption says we are not making. Sorting is by the number (the cell's value).
    title: dataset.cloakLabels.cloaking,
    rows: [
      {
        id: "cloaking",
        label: dataset.cloakLabels.cloaking,
        kinds: SHIPS_ONLY,
        cell: frameCloaking,
        format: cloakRung,
      },
    ],
  },
];

/** The antimatter pool (PRD story 15, Issue 50): what a ship spends on its abilities, and
 * the cell a Level moves most visibly — a Kol reads 225 at level 1 and 405 at level 10, so
 * ability costs read against the pool the ship really has. The label is the game's own
 * (`tooltip.unit.antimatter_label` reads exactly "Antimatter"). Exact like the other pools,
 * and no best mark: "most antimatter" across a titan and a frigate says nothing.
 *
 * It is Not Applicable with the dataset's own reason on the 109 roster units with no pool.
 *
 * Declared OUTSIDE the model on purpose — see SHEET_APPENDED_ROWS. */
const ANTIMATTER_ROW: StatRowSpec = frameRow("antimatter", "Antimatter", undefined, {
  format: exactNumber,
});

/** What the two STAT SHEET surfaces — the entity page's cards and the comparison's rails —
 * append to a named model group, keyed by that group's title. An append, never a
 * reordering (ADR 0005), so every row Game Order declares keeps its place and these follow.
 *
 * Why a row lives here rather than in STAT_MODEL: the dense Ships and Structures tables draw
 * the model, so a model row is also a column and a `?sort=` token. Antimatter earns neither,
 * because ADR 0005 places a row in Game Order from an in-game reading and there is still no
 * reading of where the game prints Antimatter. (The width argument that used to stand beside
 * that one is gone: ADR 0013 accepted a Structures column for Cloak Detection, which HAS its
 * reading — so the 23 was never a ceiling, only the count that fell out of collapsing
 * Provides.) So the sheets state Antimatter and the tables do not, which is the same escape
 * hatch the roster's own exotics column uses in the other direction.
 *
 * Why HERE rather than in either route: the entity page and the comparison must append the
 * same rows in the same order, and two route-local declarations of one sheet is exactly the
 * drift this module exists to prevent. */
export const SHEET_APPENDED_ROWS: Record<string, readonly StatRowSpec[]> = {
  Survivability: [ANTIMATTER_ROW],
};

/** A group's rows as a stat SHEET draws them: the model's own, then whatever the sheets
 * append to that group. The dense tables read `group.rows` instead — that difference IS the
 * surface scoping, and it is stated in exactly these two functions. Returns the model's own
 * array unchanged where a group has no append, so a surface can never pick up a phantom
 * row. */
export function sheetRows(group: StatGroupSpec): readonly StatRowSpec[] {
  const appended = SHEET_APPENDED_ROWS[group.title];
  return appended === undefined ? group.rows : [...group.rows, ...appended];
}

/** Whether a stat SHEET — the entity page's cards and the comparison's rail — prints this
 * group's own heading over its rows.
 *
 * It does not in two cases.
 *
 * The first is a group holding exactly one row carrying the group's own name, which is what
 * happens where the game has ONE word for both (Cloaking, Cloak Detection): the heading and
 * the row's label would print that word twice in a row, once as the card title and again
 * beside the value. The ROW keeps its label — it is what a reader reads across a comparison's
 * four columns, and dropping it would leave a value with no name — so the heading is what
 * goes. The two other one-row groups are unaffected: Orbit's row is "Civilian Orbit Slot
 * Cost", a different and more specific spelling than the group's word.
 *
 * The second is a list-valued group (Provides), where the heading is a word the GAME never
 * prints. "Provides" is ours; the game states these lines with no umbrella header at all,
 * straight after the pool rows — a Lirtestra Warfare Lab reads Durability → Armor Points →
 * Hull Points → Warfare Research Points 1 → Cloak Detection, and nothing announces that the
 * fourth line changed subject (23 screenshots, 2026-09-06, evidence in
 * .scratch/sins2-atlas/structure-tooltip-order-2026-09-06.md). Each line already carries the
 * game's own label, so the heading added a word of ours over words of the game's. The sheets
 * therefore print the lines as the continuation of the pool rows the game prints, and the
 * group keeps its name where a name is structural rather than decorative: the entity page's
 * `aria-label`, the comparison's `<tbody aria-label>`, and the dense tables' column head —
 * a table column must be named, and there "Provides" survives as our word (ADR 0008
 * addendum).
 *
 * The dense tables never ask: they draw no group headings at all, only column heads. */
export function sheetShowsHeading(group: StatGroupSpec): boolean {
  if (isLineGroup(group)) return false;
  const rows = sheetRows(group);
  const [only, ...rest] = rows;
  return !(rest.length === 0 && only !== undefined && only.label === group.title);
}

/** A group that states LINES rather than stats — Provides today.
 *
 * ONE predicate, because two surfaces act on the answer and they had drifted (found in review
 * 2026-09-07): `sheetShowsHeading` asked only whether the group declares lines, while the
 * entity page also required it to declare no stat rows before drawing it inside the
 * Survivability card. A group carrying both would therefore have rendered as an ordinary card
 * with no heading at all — a nameless box, the exact shape a review finding already made
 * `StatGroup.title` required to prevent. Unreachable today, since Provides is the only line
 * group and has no rows, which is precisely why it would have shipped in silence.
 *
 * The stricter reading wins: a group is line-valued when its whole content is lines. One that
 * grew stat rows would keep its heading and its own card, which is the safe direction — a
 * visible heading over real rows, rather than rows tucked namelessly under another group. */
export function isLineGroup(group: StatGroupSpec): boolean {
  return group.lines !== undefined && group.rows.length === 0;
}

/** The model as one view or the comparison renders it: the same groups in the same order,
 * each holding only the rows that apply to at least one of `kinds`. Ships alone drops the
 * Orbit group (its one row is structures-only); Structures alone drops the ships-only rows;
 * both together is the whole model (a starbase beside a titan shows every row, the union
 * rule of ADR 0008); no kinds is no rows. Rows keep their identity, so a filtered group's
 * rows are the model's own. A group left with no rows is dropped rather than rendered as a
 * bare heading — Orbit on Ships is one case, and a list-valued group (Provides) stays
 * exactly when its own kinds match, its rows being read off the units later. */
export function statModelFor(kinds: readonly ViewKind[]): readonly StatGroupSpec[] {
  const wanted = new Set(kinds);
  return STAT_MODEL.flatMap((group) => {
    const rows = group.rows.filter((row) => row.kinds.some((kind) => wanted.has(kind)));
    const lines = group.lines?.kinds.some((kind) => wanted.has(kind)) ?? false;
    if (rows.length === 0 && !lines) return [];
    return [rows.length === group.rows.length ? group : { ...group, rows }];
  });
}

/** A group by title, for the model's own tests and the two route tests that pin what a surface
 * appends — no surface calls it (review 2026-09-08); each appends through SHEET_APPENDED_ROWS. */
export function groupOf(title: string): StatGroupSpec {
  const group = STAT_MODEL.find((candidate) => candidate.title === title);
  if (group === undefined) {
    throw new Error(`stat-model: no group titled "${title}" — the model and a surface disagree`);
  }
  return group;
}

/** The roster's column header for a row: its dense spelling where one is earned. */
export function headerLabel(row: StatRowSpec): string {
  return row.shortLabel ?? row.label;
}
